/**
 * PSE-4 — alternative-option recommendation engine.
 *
 * When the narrative recommends a BTM tech whose local public sentiment
 * is oppositional, this module looks across the rest of the
 * `perTechSentiment` payload for a sentiment-FRIENDLIER alternative
 * tech that is also PLAUSIBLE for the site profile, and emits a
 * structured `AltOptionFinding[]` the hygiene hook
 * (`applyAltOptionRecommendations`) uses to append a one-sentence
 * advisory to the offending recommended-action.
 *
 * Composition with PSE-3:
 *   - PSE-3 (`flagSentimentConflicts`) softens an assertive verb on a
 *     tech with oppositional sentiment and stamps the
 *     `pse-sentiment-conflict` warning. It does NOT suggest what to do
 *     instead.
 *   - PSE-4 (this module) consumes the SAME `perTechSentiment` payload
 *     PSE-3 reads and proposes a constructive next step
 *     ("Alternatively, consider <alt-tech>, which has stronger
 *     community sentiment in this jurisdiction (X% vs Y%)").
 *
 * The two are independent: PSE-3 can fire without PSE-4 (no plausible
 * alt available) and PSE-4 can fire on a tech PSE-3 already softened
 * (the soften-prefix is informational, the alt-option sentence is
 * additive).
 *
 * Hard rules honoured (pack §1.4 + the user prompt):
 *   - Pure / synchronous / NEVER throws. A bad input → empty array.
 *   - `Number.isFinite()` strict guards on every score + percentage.
 *   - Conservative — better to skip a recommendation than make a bad
 *     one. Both the sentiment-delta threshold (≥ +0.4) AND the
 *     plausibility filter (per-tech site-profile guards) must clear.
 *   - Never recommend a tech the narrative ALREADY names as an
 *     alternative — we don't want a double-suggestion / loop.
 *   - Reuse PSE-3's primitives: `PSE_TECH_PATTERNS` regex table for
 *     "which tech is this prose talking about" detection.
 */
import type { PerTechSentimentInput } from '../narrativeHygiene';
import { __INTERNAL_PSE } from '../narrativeHygiene';

/* ── Public types ──────────────────────────────────────────────────── */

/**
 * Site-profile inputs the plausibility filter consults. Every field is
 * optional — an unknown field means "no signal; don't penalise". The
 * filter is conservative: a tech is plausible-by-default unless we have
 * a positive signal it is NOT (e.g. parcel demonstrably < 50 acres).
 *
 * Sources at the call site:
 *   - `parcelAcres`              ← `sed.parcel.data.acres` or
 *                                  `report.siteArea` (legacy alias).
 *   - `solarGhi`                 ← `sed.solar.data.ghi` (kWh/m²/day).
 *   - `windAvgSpeed`             ← `sed.wind.data.avgSpeed` (m/s).
 *   - `gasInfrastructureAvailable`
 *                                ← `sed.grid.data.gasInfrastructure`
 *                                  or `false` when a regulatory layer
 *                                  excludes gas explicitly.
 *   - `radiationProtected`       ← truthy when the site sits inside a
 *                                  high-radiation exclusion zone
 *                                  (legacy "radiation protected"
 *                                  designation).
 *   - `excludedTechs`            ← `projectIntent.excludedPowerSources`
 *                                  when the user explicitly disallowed
 *                                  alternates (pack R9 mitigation).
 */
export interface AltOptionSiteProfile {
  parcelAcres?: number;
  solarGhi?: number;
  windAvgSpeed?: number;
  gasInfrastructureAvailable?: boolean;
  radiationProtected?: boolean;
  excludedTechs?: ReadonlyArray<string>;
}

/**
 * One alt-option recommendation. Emitted once per oppositional-tech
 * the engine decided to surface a swap for.
 */
export interface AltOptionFinding {
  /** BTM tech the narrative recommended (the oppositional one). */
  fromTech: string;
  /** BTM tech we're suggesting instead. */
  toTech: string;
  /** From-tech mean sentiment (in [-1, 0], oppositional). */
  fromSentiment: number;
  /** To-tech mean sentiment (in [0, +1], supportive). */
  toSentiment: number;
  /** Sentiment delta `toSentiment - fromSentiment` (always ≥ 0.4). */
  sentimentDelta: number;
  /** Positive-share % rendered in the appended sentence (0-100 int). */
  positivePct: number;
  /** Negative-share % rendered in the appended sentence (0-100 int). */
  negativePct: number;
  /** Human-readable swap sentence ready to be appended. */
  sentence: string;
}

/* ── Tunables ──────────────────────────────────────────────────────── */

/**
 * Minimum sentiment-delta (toTech.score − fromTech.score) before we
 * recommend a swap. ≥ +0.4 is intentionally conservative — a swing
 * smaller than that within the [-1, +1] axis is noise-level and we'd
 * rather under-recommend.
 */
export const PSE4_MIN_SENTIMENT_DELTA = 0.4;

/**
 * Lower bound on the alt-tech's mean sentiment. < 0 means "still
 * net-oppositional"; an alt-tech with `meanSentiment ≤ 0` is not a
 * meaningful upgrade even if its delta clears the threshold.
 */
export const PSE4_MIN_ALT_SCORE = 0.0;

/**
 * Lower bound on the alt-tech's sample count. Mirrors PSE-3's
 * `PSE_MIN_SAMPLE_COUNT` so we don't recommend a tech whose positive
 * score is statistically thin.
 */
export const PSE4_MIN_ALT_COUNT = 5;

/**
 * From-tech threshold for being considered "oppositional". Matches
 * PSE-3's `PSE_OPPOSITIONAL_THRESHOLD` for cross-pack consistency.
 */
export const PSE4_FROM_OPPOSITIONAL_THRESHOLD = -0.2;

/**
 * Cap on samples returned per call. Five oppositional techs is already
 * an extreme failure case; in practice 1-2 swaps will fire.
 */
export const PSE4_MAX_FINDINGS = 5;

/* ── Plausibility filter ───────────────────────────────────────────── */

/**
 * Per-tech site-profile floor table. Used by `isAltTechPlausible` to
 * decide whether a tech is feasible at the site. All thresholds are
 * conservative — the floor is set well below the engineering
 * sweet-spot so we don't FALSE-REJECT a viable alternative, just
 * reject demonstrably unworkable ones.
 *
 *   - nuclear-smr: needs ≥ 50 acres. Smaller parcels can't site even a
 *     compact SMR (NRC siting guidance + buffer + admin area). The
 *     pack-spec example threshold.
 *   - solar-bess: needs GHI ≥ 3.5 kWh/m²/day. Below that a utility-
 *     scale solar farm is economically marginal regardless of
 *     sentiment.
 *   - wind-bess: needs avg wind speed ≥ 5 m/s @ ~80m hub height.
 *     Below that the LCOE is uncompetitive for grid-scale.
 *   - natural-gas: needs `gasInfrastructureAvailable !== false`.
 *     An explicit "no pipeline within 50mi" sed flag rules it out.
 *   - general-datacenter: always plausible (it's a power-source-
 *     neutral wrapper, not a generation tech).
 */
const PSE4_PARCEL_ACRES_MIN_NUCLEAR = 50;
const PSE4_GHI_MIN_SOLAR = 3.5;
const PSE4_WIND_SPEED_MIN = 5.0;

/**
 * Heuristic plausibility check. Returns `true` when the alt-tech is
 * feasible for the site (the swap is safe to recommend).
 *
 * Defensive bounds:
 *   - Non-finite numeric inputs are treated as "unknown" (allow).
 *   - The `radiationProtected` flag is consulted only for solar — that's
 *     the only tech where a "radiation protected" exclusion zone
 *     (typically a low-radiation watershed / habitat overlay) excludes
 *     ground-mount arrays.
 *   - `excludedTechs` is the user's explicit veto list (PSE pack R9
 *     mitigation). If the alt-tech appears there, we never recommend
 *     it.
 */
export function isAltTechPlausible(
  tech: string,
  profile: AltOptionSiteProfile | undefined,
): boolean {
  if (!profile) return true; // No profile → allow (conservative-on-recommend side).

  // Explicit user veto wins regardless of other signals.
  if (profile.excludedTechs?.some(t => t === tech)) return false;

  switch (tech) {
    case 'nuclear-smr': {
      if (
        typeof profile.parcelAcres === 'number' &&
        Number.isFinite(profile.parcelAcres) &&
        profile.parcelAcres > 0 &&
        profile.parcelAcres < PSE4_PARCEL_ACRES_MIN_NUCLEAR
      ) {
        return false;
      }
      return true;
    }
    case 'solar-bess': {
      if (profile.radiationProtected === true) return false;
      if (
        typeof profile.solarGhi === 'number' &&
        Number.isFinite(profile.solarGhi) &&
        profile.solarGhi > 0 &&
        profile.solarGhi < PSE4_GHI_MIN_SOLAR
      ) {
        return false;
      }
      return true;
    }
    case 'wind-bess': {
      if (
        typeof profile.windAvgSpeed === 'number' &&
        Number.isFinite(profile.windAvgSpeed) &&
        profile.windAvgSpeed > 0 &&
        profile.windAvgSpeed < PSE4_WIND_SPEED_MIN
      ) {
        return false;
      }
      return true;
    }
    case 'natural-gas': {
      if (profile.gasInfrastructureAvailable === false) return false;
      return true;
    }
    case 'general-datacenter':
      return true;
    default:
      // Unknown tech — refuse to recommend it. We only recommend techs
      // we have a plausibility rule for.
      return false;
  }
}

/* ── Narrative scanning helpers ────────────────────────────────────── */

/**
 * Does `narrativeText` contain an assertive mention of `tech` (the
 * shape PSE-3's regex table matches)? Used to gate whether the engine
 * even bothers looking for an alt — if the narrative doesn't recommend
 * the tech in the first place, there's nothing to swap.
 *
 * Pure. Returns `false` on missing input.
 */
export function narrativeRecommendsTech(narrativeText: string | null | undefined, tech: string): boolean {
  if (!narrativeText || typeof narrativeText !== 'string') return false;
  const re = __INTERNAL_PSE.PSE_TECH_PATTERNS[tech];
  if (!re) return false;
  // Fresh regex per call — shared regex state across multi-call probes
  // would skip matches via stale `.lastIndex`.
  const localRe = new RegExp(re.source, re.flags);
  return localRe.test(narrativeText);
}

/**
 * Does `narrativeText` already mention `tech` in any form? Used to
 * avoid double-recommending an alt that's already on the page. We use
 * a broader noun-only regex (not the assertive-verb pattern) so a
 * mere "wind farms are also being considered" suppresses a wind-bess
 * swap.
 *
 * Per-tech noun-only regexes mirror the noun-phrase tail of
 * `PSE_TECH_PATTERNS` so the surface area stays consistent.
 */
const PSE4_TECH_NOUN_PATTERNS: Record<string, RegExp> = {
  'nuclear-smr':
    /\b(?:SMR|nuclear|small\s+modular\s+reactor)\b/i,
  'natural-gas':
    /\b(?:natural\s+gas|gas\s+turbine|combined[-\s]?cycle|CCGT)\b/i,
  'solar-bess':
    /\b(?:solar(?:\s*\+\s*BESS)?|photovoltaic|PV(?:\s*\+\s*storage)?|utility[-\s]?scale\s+solar)\b/i,
  'wind-bess':
    /\b(?:wind(?:\s*\+\s*BESS)?|wind\s+turbine|wind\s+farm)\b/i,
  'general-datacenter':
    /\b(?:data\s*center|datacenter|hyperscale)\b/i,
};

export function narrativeMentionsTech(narrativeText: string | null | undefined, tech: string): boolean {
  if (!narrativeText || typeof narrativeText !== 'string') return false;
  const re = PSE4_TECH_NOUN_PATTERNS[tech];
  if (!re) return false;
  return re.test(narrativeText);
}

/* ── Sentence composer ────────────────────────────────────────────── */

/**
 * Human-readable label for the BTM tech (singular, no leading article).
 * Used inside the appended sentence so the prose reads naturally.
 */
const PSE4_TECH_LABELS: Record<string, string> = {
  'nuclear-smr': 'solar+BESS or natural-gas',           // placeholder; not used as label
  'natural-gas': 'natural gas',
  'solar-bess': 'solar+BESS',
  'wind-bess': 'wind+BESS',
  'general-datacenter': 'a more flexible data-center configuration',
};

/** Map an alt-tech to its display label. Falls back to the id. */
function altTechLabel(tech: string): string {
  switch (tech) {
    case 'nuclear-smr': return 'nuclear SMR';
    case 'natural-gas': return 'natural gas';
    case 'solar-bess': return 'solar+BESS';
    case 'wind-bess': return 'wind+BESS';
    case 'general-datacenter': return 'a more flexible data-center configuration';
    default: return tech;
  }
}

/**
 * Convert a sentiment score in [-1, +1] to an integer positive-share
 * percentage 0..100 the appended sentence renders. We treat
 * `meanSentiment` as the centroid of a [-1, +1] distribution and map
 *   score = +1 → 100 % positive
 *   score =  0 →  50 %
 *   score = -1 →   0 %
 * Linear, clamped, integer-rounded. Defensive on non-finite input.
 */
export function sentimentToPositivePct(score: number): number {
  if (!Number.isFinite(score)) return 0;
  const clamped = Math.max(-1, Math.min(1, score));
  return Math.round(((clamped + 1) / 2) * 100);
}

/**
 * Mirror of `sentimentToPositivePct` for the negative share. Always
 * `100 − positivePct` because the model is single-axis.
 */
export function sentimentToNegativePct(score: number): number {
  return 100 - sentimentToPositivePct(score);
}

/**
 * Compose the swap sentence. Exact phrasing pinned by the prompt brief
 * so the renderer tooltip / search can locate it deterministically.
 */
export function composeAltOptionSentence(finding: Omit<AltOptionFinding, 'sentence'>): string {
  const label = altTechLabel(finding.toTech);
  return (
    `Alternatively, consider ${label}, which has stronger community sentiment in this jurisdiction ` +
    `(${finding.positivePct}% positive vs ${finding.negativePct}% negative).`
  );
}

/* ── Main entry point ──────────────────────────────────────────────── */

/**
 * Inputs to `recommendAlternativeOptions`. Kept as a tagged object so
 * the call sites in `narrativeHygiene` can pass through `siteProfile`
 * without re-shuffling positional args.
 */
export interface RecommendAlternativeOptionsArgs {
  /** The same `perTechSentiment` PSE-3 reads (5 fixed BTM-tech buckets). */
  perTechSentiment: ReadonlyArray<PerTechSentimentInput> | null | undefined;
  /** Rendered narrative body — concatenation of report fields the
   *  hygiene pipeline cares about, lower-cased + joined for scanning. */
  narrativeText: string | null | undefined;
  /** Site-profile flags for the plausibility filter. Optional — when
   *  absent, plausibility falls back to allow-by-default. */
  siteProfile?: AltOptionSiteProfile;
}

/**
 * Build `AltOptionFinding[]` for the report. Empty array when:
 *   - perTechSentiment is null / empty.
 *   - No tech is oppositional with sufficient evidence.
 *   - The narrative doesn't recommend any of the oppositional techs.
 *   - No alt-tech clears the sentiment-delta + plausibility gates.
 *
 * Algorithm (per oppositional tech):
 *   1. Skip if `from.meanSentiment > -0.2` OR `count < 5`.
 *   2. Skip if the narrative doesn't ASSERTIVELY recommend the
 *      from-tech (PSE-3's pattern). No assertive prose → no
 *      intervention to attach to.
 *   3. Rank the OTHER techs in `perTechSentiment` by sentiment-delta
 *      descending. For each candidate alt:
 *        a. Skip if `alt.meanSentiment ≤ 0` OR `alt.count < 5`.
 *        b. Skip if `delta < 0.4`.
 *        c. Skip if `!isAltTechPlausible(alt, profile)`.
 *        d. Skip if the narrative already MENTIONS the alt-tech.
 *        e. First alt that survives → emit a finding; stop.
 *   4. Cap total findings at `PSE4_MAX_FINDINGS`.
 *
 * Never throws. Conservative — better to emit zero than a bad swap.
 */
export function recommendAlternativeOptions(args: RecommendAlternativeOptionsArgs): AltOptionFinding[] {
  const { perTechSentiment, narrativeText, siteProfile } = args ?? ({} as RecommendAlternativeOptionsArgs);
  if (!perTechSentiment || perTechSentiment.length === 0) return [];
  if (!narrativeText || typeof narrativeText !== 'string' || narrativeText.length === 0) return [];

  // Normalise + validate every bucket up front so the ranker doesn't
  // re-check defensively on every comparison.
  type NormBucket = {
    btmTech: string;
    score: number;
    count: number;
  };
  const buckets: NormBucket[] = [];
  for (const t of perTechSentiment) {
    if (!t || typeof t.btmTech !== 'string') continue;
    const score = Number.isFinite(t.meanSentiment) ? t.meanSentiment : Number.NaN;
    const count = Number.isFinite(t.count) && t.count > 0 ? t.count : 0;
    if (!Number.isFinite(score)) continue;
    buckets.push({ btmTech: t.btmTech, score, count });
  }
  if (buckets.length === 0) return [];

  // Build a quick lookup table.
  const byTech = new Map<string, NormBucket>();
  for (const b of buckets) byTech.set(b.btmTech, b);

  // Pre-rank candidate alts by sentiment-score descending. Same order
  // for every from-tech (we don't carry per-from biases).
  const altCandidates = [...buckets].sort((a, b) => b.score - a.score);

  const findings: AltOptionFinding[] = [];

  for (const from of buckets) {
    if (findings.length >= PSE4_MAX_FINDINGS) break;

    // Gate 1: from must be oppositional with enough evidence.
    if (from.score > PSE4_FROM_OPPOSITIONAL_THRESHOLD) continue;
    if (from.count < PSE4_MIN_ALT_COUNT) continue;

    // Gate 2: pattern table must know how to detect this tech (so we
    // can prove the narrative actually recommends it). Unknown techs
    // are silently skipped.
    if (!__INTERNAL_PSE.PSE_TECH_PATTERNS[from.btmTech]) continue;

    // Gate 3: narrative must ASSERTIVELY recommend from-tech.
    if (!narrativeRecommendsTech(narrativeText, from.btmTech)) continue;

    // Search ranked candidates for the first plausible alt.
    let chosen: NormBucket | null = null;
    for (const alt of altCandidates) {
      if (alt.btmTech === from.btmTech) continue;
      if (alt.score <= PSE4_MIN_ALT_SCORE) continue;
      if (alt.count < PSE4_MIN_ALT_COUNT) continue;
      const delta = alt.score - from.score;
      if (!Number.isFinite(delta) || delta < PSE4_MIN_SENTIMENT_DELTA) continue;
      if (!isAltTechPlausible(alt.btmTech, siteProfile)) continue;
      // Don't double-recommend a tech the narrative already names.
      if (narrativeMentionsTech(narrativeText, alt.btmTech)) continue;
      chosen = alt;
      break;
    }

    if (!chosen) continue;

    const positivePct = sentimentToPositivePct(chosen.score);
    const negativePct = sentimentToNegativePct(from.score);
    const partial: Omit<AltOptionFinding, 'sentence'> = {
      fromTech: from.btmTech,
      toTech: chosen.btmTech,
      fromSentiment: from.score,
      toSentiment: chosen.score,
      sentimentDelta: chosen.score - from.score,
      positivePct,
      negativePct,
    };
    findings.push({ ...partial, sentence: composeAltOptionSentence(partial) });
  }

  return findings;
}

/* ── Internal exports — tests only ─────────────────────────────────── */

/** Test-only handle for inspecting the noun-pattern table + label map
 *  without spreading regex literals across the test file. */
export const __INTERNAL_PSE4 = {
  PSE4_TECH_NOUN_PATTERNS,
  PSE4_TECH_LABELS,
  altTechLabel,
} as const;
